import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";

import "../../css/CustomTable.css";

const missing = (value) => value == null || value === "";
const valueKey = (value) =>
  missing(value) ? "missing:" : `${typeof value}:${String(value)}`;
const displayValue = (value, column) => {
  if (missing(value)) return String(column.default ?? "");

  const formatted =
    typeof column.formatValue === "function"
      ? column.formatValue(value)
      : value;

  return missing(formatted) ? String(column.default ?? "") : String(formatted);
};
// colorRule: { key?, mode: "text" | "badge" | "cell" | "row", values, fallback? }.
// Map raw field values to { color, background }; key defaults to the column key.
const resolveColor = (rule, row, key) => {
  if (!rule || !row) return null;
  const value = row[rule.key ?? key];
  const palette = Object.prototype.hasOwnProperty.call(rule.values || {}, value)
    ? rule.values[value]
    : rule.fallback;
  return palette ? { ...palette, mode: rule.mode || "text" } : null;
};
const compare = (a, b) => {
  if (missing(a)) return missing(b) ? 0 : 1;
  if (missing(b)) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: "base"
  });
};

function OverflowText({
  as: Tag = "span",
  text,
  shortText,
  truncate,
  className,
  children,
  ...props
}) {
  const tooltipId = useId();
  const anchor = useRef(null);
  const bubble = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const [abbreviated, setAbbreviated] = useState(false);
  const middle = truncate?.mode === "middle";
  const characters = Array.from(String(text ?? ""));
  const tailLength =
    Number.isInteger(truncate?.tailLength) && truncate.tailLength > 0
      ? truncate.tailLength
      : 6;
  const splitAt = Math.max(0, characters.length - tailLength);
  useLayoutEffect(() => {
    const label = anchor.current.querySelector(".customtable-header-text");
    const measure = anchor.current.querySelector(".customtable-header-measure");
    if (!label || !measure || !shortText) {
      setAbbreviated(false);
      return undefined;
    }
    const update = () =>
      setAbbreviated(measure.getBoundingClientRect().width > label.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(label);
    observer.observe(measure);
    return () => observer.disconnect();
  }, [text, shortText]);
  const show = () => {
    const textElement =
      anchor.current.querySelector(
        ".customtable-header-text, .customtable-middle-start"
      ) || anchor.current;
    if (
      abbreviated ||
      textElement.scrollWidth > textElement.clientWidth ||
      anchor.current.scrollWidth > anchor.current.clientWidth
    ) {
      setPosition(null);
      setOpen(true);
    }
  };

  useLayoutEffect(() => {
    if (!open) return;
    const rect = anchor.current.getBoundingClientRect();
    const size = bubble.current.getBoundingClientRect();
    const left = Math.max(
      8,
      Math.min(rect.left, window.innerWidth - size.width - 8)
    );
    const below = rect.bottom + 8;
    const top =
      below + size.height <= window.innerHeight - 8
        ? below
        : Math.max(8, rect.top - size.height - 8);
    setPosition({ left, top });
  }, [open, text]);

  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", close);
    };
  }, [open]);

  return (
    <>
      <Tag
        {...props}
        className={`${className || ""}${middle ? " customtable-middle-text" : ""}`}
        ref={anchor}
        aria-describedby={open ? tooltipId : undefined}
        onMouseEnter={show}
        onMouseLeave={() => setOpen(false)}
        onFocus={show}
        onBlur={() => setOpen(false)}
      >
        {middle ? (
          <>
            <span className="customtable-middle-start">
              {characters.slice(0, splitAt).join("")}
            </span>
            <span className="customtable-middle-end">
              {characters.slice(splitAt).join("")}
            </span>
          </>
        ) : typeof children === "function" ? (
          children(abbreviated ? shortText : text)
        ) : (
          (children ?? text)
        )}
      </Tag>
      {open &&
        createPortal(
          <div
            ref={bubble}
            id={tooltipId}
            role="tooltip"
            className={`customtable-tooltip${position ? " customtable-tooltip-visible" : ""}`}
            style={{
              "--customtable-tooltip-left": `${position?.left ?? 0}px`,
              "--customtable-tooltip-top": `${position?.top ?? 0}px`
            }}
          >
            {text}
          </div>,
          document.body
        )}
    </>
  );
}

/**
 * Client-side table. config.rowKey is a field name or (row) => stableId.
 * onEdit(row, newValue) may return a promise; the caller updates data after saving.
 * config.onRowClick(row) handles navigation. config.pageSize defaults to 25.
 * showPageSizeSelector and showTotalCount are opt-in, independent flags.
 * pageSizeOptions supplies the selector choices (default: 10, 25, 50, 100).
 * allowTextSelection enables selecting header and row text (disabled by default).
 * columns[].fixed pins a column to the left while scrolling horizontally.
 * columns[].shortHeader replaces the full header when it does not fit.
 * columns[].truncate: { mode: "middle", tailLength: 6 } preserves the last characters.
 * columns[].render(value, row) renders custom cell content, e.g. image previews.
 * Optional config.labels overrides the Hungarian control labels.
 */
export default function CustomTable({ data = [], config, loading = false }) {
  const id = useId();
  const root = useRef(null);
  const pendingRowFocus = useRef(null);
  const saving = useRef(false);
  const cancelEdit = useRef(false);
  const [page, setPage] = useState(1);
  const [fixedOffsets, setFixedOffsets] = useState({});
  const [selectedPageSize, setSelectedPageSize] = useState(null);
  const [sort, setSort] = useState(null);
  const [filters, setFilters] = useState({});
  const [popup, setPopup] = useState(null);
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState([]);
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const clickTimer = useRef(null);
  const labels = {
    first: "Első",
    previous: "Előző",
    next: "Következő",
    last: "Utolsó",
    page: "Oldal",
    filter: "Szűrés",
    search: "Érték keresése",
    all: "Összes kijelölése",
    apply: "Alkalmaz",
    clear: "Szűrés törlése",
    actions: "Műveletek",
    loading: "Betöltés…",
    noMatches: "Nincs találat.",
    saveError: "A mentés sikertelen. Próbáld újra.",
    edit: "Szerkesztés",
    rowsPerPage: "Sor / oldal",
    total: "Összesen",
    matches: "Találat",
    items: "db",
    ...config.labels
  };
  const columns = config.columns;

  const isSortable = (column) =>
    !column.actionlist && column.sortable !== false;

  const isFilterable = (column) =>
    !column.actionlist && column.filterable !== false;
  useLayoutEffect(() => {
    const headers = root.current.querySelectorAll("thead th");
    const updateOffsets = () => {
      let left = 0;
      const offsets = {};
      columns.forEach((column, index) => {
        if (!column.fixed) return;
        offsets[column.key] = left;
        left += headers[index].getBoundingClientRect().width;
      });
      setFixedOffsets((previous) =>
        JSON.stringify(previous) === JSON.stringify(offsets)
          ? previous
          : offsets
      );
    };
    updateOffsets();
    const observer = new ResizeObserver(updateOffsets);
    headers.forEach((header) => observer.observe(header));
    return () => observer.disconnect();
  }, [columns]);
  const cellStyle = (column) => ({
    "--customtable-column-align": column.align || "left",
    "--customtable-fixed-left": `${fixedOffsets[column.key] ?? 0}px`
  });
  const recordStyle = (column, row) => {
    const tone = resolveColor(column.colorRule, row, column.key);
    const rowColumn = columns.find(
      (entry) =>
        entry.colorRule?.mode === "row" &&
        resolveColor(entry.colorRule, row, entry.key)
    );
    const rowTone =
      resolveColor(config.rowColorRule, row) ||
      (rowColumn && resolveColor(rowColumn.colorRule, row, rowColumn.key));
    const fill = tone?.mode === "cell" ? tone : rowTone;
    return {
      ...cellStyle(column),
      "--customtable-value-color": tone?.color,
      "--customtable-value-background": tone?.background,
      "--customtable-fill": fill?.background,
      "--customtable-fill-color": fill?.color
    };
  };
  const defaultPageSize =
    Number.isFinite(config.pageSize) && config.pageSize > 0
      ? Math.max(1, Math.floor(config.pageSize))
      : 25;
  const pageSize = config.showPageSizeSelector
    ? (selectedPageSize ?? defaultPageSize)
    : defaultPageSize;
  const pageSizeOptions = [
    ...new Set(
      [
        defaultPageSize,
        pageSize,
        ...(Array.isArray(config.pageSizeOptions)
          ? config.pageSizeOptions
          : [10, 25, 50, 100])
      ].filter((size) => Number.isInteger(size) && size > 0)
    )
  ].sort((a, b) => a - b);
  const getId = (row) =>
    typeof config.rowKey === "function"
      ? config.rowKey(row)
      : row[config.rowKey];
  const matchesFilters = (row, exceptKey) =>
    columns.every(
      (column) =>
        column.key === exceptKey ||
        !isFilterable(column) ||
        !filters[column.key] ||
        filters[column.key].includes(valueKey(row[column.key]))
    );
  const filtered = data.filter((row) => matchesFilters(row));

  if (
    sort &&
    columns.some((column) => column.key === sort.key && isSortable(column))
  ) {
    const column = columns.find((entry) => entry.key === sort.key);
    const sortValue = column.sortValue || ((row) => row[sort.key]);

    filtered.sort(
      (a, b) => compare(sortValue(a), sortValue(b)) * sort.direction
    );
  }
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, pages);
  const rows = filtered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  useLayoutEffect(() => {
    if (pendingRowFocus.current === null || loading) return;
    const rowElements = root.current.querySelectorAll("tbody tr[data-record]");
    const index = Math.min(pendingRowFocus.current, rowElements.length - 1);
    pendingRowFocus.current = null;
    rowElements[index]?.focus();
    rowElements[index]?.scrollIntoView?.({
      block: "nearest",
      inline: "nearest"
    });
  }, [currentPage, rows.length, loading]);

  function handleTableKeyDown(event) {
    if (
      event.defaultPrevented ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey ||
      busy ||
      loading ||
      popup ||
      edit ||
      event.target.closest(
        "input, textarea, select, [contenteditable]:not([contenteditable=false]), .customtable-popup"
      )
    )
      return;
    const direction = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -1,
      ArrowDown: 1
    }[event.key];
    if (!direction) return;
    event.preventDefault();
    clearTimeout(clickTimer.current);
    const rowElements = Array.from(
      root.current.querySelectorAll("tbody tr[data-record]")
    );
    const activeRow = event.target.closest("tr[data-record]");
    const index = rowElements.indexOf(activeRow);
    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      const nextPage = currentPage + direction;
      if (nextPage < 1 || nextPage > pages) return;
      pendingRowFocus.current = Math.max(0, index);
      setPage(nextPage);
      return;
    }
    const nextIndex =
      index < 0
        ? direction > 0
          ? 0
          : rowElements.length - 1
        : index + direction;
    if (nextIndex >= 0 && nextIndex < rowElements.length) {
      rowElements[nextIndex].focus();
      rowElements[nextIndex].scrollIntoView?.({
        block: "nearest",
        inline: "nearest"
      });
    } else if (
      index >= 0 &&
      currentPage + direction >= 1 &&
      currentPage + direction <= pages
    ) {
      pendingRowFocus.current = direction > 0 ? 0 : pageSize - 1;
      setPage(currentPage + direction);
    }
  }

  useEffect(() => {
    setPage((previous) => Math.min(previous, pages));
  }, [pages]);
  useEffect(() => {
    setSelectedPageSize(null);
    setPage(1);
  }, [defaultPageSize, config.showPageSizeSelector]);
  useEffect(() => () => clearTimeout(clickTimer.current), []);
  useEffect(() => {
    if (!popup) return undefined;
    const close = (event) => {
      if (event.type === "keydown" && event.key !== "Escape") return;
      if (
        event.type === "pointerdown" &&
        root.current?.contains(event.target)
      ) {
        if (event.target.closest(".customtable-popup, [data-popup-trigger]"))
          return;
      }
      setPopup(null);
      if (event.type === "keydown") popup.trigger?.focus();
    };
    document.addEventListener("pointerdown", close);
    document.addEventListener("keydown", close);
    window.addEventListener("resize", close);
    const scroll = (event) => {
      if (!event.target.closest?.(".customtable-popup")) setPopup(null);
    };
    window.addEventListener("scroll", scroll, true);
    return () => {
      document.removeEventListener("pointerdown", close);
      document.removeEventListener("keydown", close);
      window.removeEventListener("resize", close);
      window.removeEventListener("scroll", scroll, true);
    };
  }, [popup]);

  function openPopup(event, details) {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    setPopup({
      ...details,
      trigger: event.currentTarget,
      left: Math.max(8, Math.min(rect.left, window.innerWidth - 276)),
      ...(window.innerHeight - rect.bottom < 340 &&
      rect.top > window.innerHeight / 2
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 })
    });
  }
  function optionsFor(column) {
    const availableRows = data.filter((row) => matchesFilters(row, column.key));
    return Array.from(
      new Map(
        availableRows.map((row) => [valueKey(row[column.key]), row[column.key]])
      ).entries()
    ).sort((a, b) => compare(a[1], b[1]));
  }
  function beginEdit(row, column) {
    clearTimeout(clickTimer.current);
    if (saving.current) return;
    cancelEdit.current = false;
    setError("");
    setEdit({
      rowId: getId(row),
      column: column.key,
      value: row[column.key] ?? "",
      row
    });
  }
  async function saveEdit(column) {
    if (!edit || saving.current || cancelEdit.current) return;
    if (String(edit.value) === String(edit.row[column.key] ?? "")) {
      setEdit(null);
      return;
    }
    saving.current = true;
    setBusy(true);
    setError("");
    try {
      await column.onEdit(edit.row, edit.value);
      setEdit(null);
    } catch (failure) {
      setError(labels.saveError);
    } finally {
      saving.current = false;
      setBusy(false);
    }
  }
  function navigation(position) {
    const pageSlots = [
      currentPage > 2 ? 1 : null,
      currentPage > 3 ? "gap" : null,
      currentPage > 1 ? currentPage - 1 : null,
      currentPage,
      currentPage < pages ? currentPage + 1 : null,
      currentPage < pages - 2 ? "gap" : null,
      currentPage < pages - 1 ? pages : null
    ];
    return (
      <nav
        className="customtable-nav"
        aria-label={`${labels.page} (${position})`}
      >
        {(config.showPageSizeSelector || config.showTotalCount) && (
          <div className="customtable-nav-summary">
            {config.showPageSizeSelector && (
              <label className="customtable-page-size">
                {labels.rowsPerPage}
                <select
                  value={pageSize}
                  disabled={busy || loading}
                  onChange={(event) => {
                    setSelectedPageSize(Number(event.target.value));
                    setPage(1);
                    setPopup(null);
                    root.current
                      ?.querySelector(".customtable-scroll")
                      ?.scrollTo?.({ top: 0 });
                  }}
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {config.showTotalCount && (
              <span className="customtable-count" aria-live="polite">
                {filtered.length ? (currentPage - 1) * pageSize + 1 : 0}-
                {Math.min(currentPage * pageSize, filtered.length)}/
                {filtered.length}
              </span>
            )}
          </div>
        )}
        {pages > 1 && (
          <div className="customtable-nav-buttons">
            {pageSlots.map((number, index) =>
              number === null || number === "gap" ? (
                <span
                  key={index}
                  className="customtable-page-gap"
                  aria-hidden="true"
                >
                  {number === "gap" ? "…" : ""}
                </span>
              ) : (
                <button
                  key={index}
                  type="button"
                  aria-label={`${labels.page} ${number}`}
                  aria-current={number === currentPage ? "page" : undefined}
                  disabled={busy || loading}
                  onClick={() => setPage(number)}
                >
                  {number}
                </button>
              )
            )}
          </div>
        )}
      </nav>
    );
  }

  return (
    <div
      className={`customtable${config.allowTextSelection === true ? " customtable-selectable" : ""}`}
      ref={root}
      tabIndex={0}
      onKeyDown={handleTableKeyDown}
      aria-busy={loading || busy}
    >
      <div
        className="customtable-scroll"
        onScroll={(event) => {
          event.currentTarget.classList.toggle(
            "customtable-scrolled",
            event.currentTarget.scrollLeft > 0
          );
        }}
      >
        <table>
          <colgroup>
            {columns.map((column) => (
              <col
                key={column.key}
                className="customtable-column"
                style={{
                  "--customtable-column-width":
                    typeof column.width === "number"
                      ? `${column.width}px`
                      : column.width
                }}
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  style={cellStyle(column)}
                  data-fixed={column.fixed ? "true" : undefined}
                  className={
                    filters[column.key]
                      ? "customtable-filtered-column"
                      : undefined
                  }
                  aria-sort={
                    !isSortable(column)
                      ? undefined
                      : sort?.key === column.key
                        ? sort.direction === 1
                          ? "ascending"
                          : "descending"
                        : "none"
                  }
                >
                  {column.actionlist ? (
                    column.header
                  ) : (
                    <div className="customtable-heading">
                      {isSortable(column) ? (
                        <OverflowText
                          as="button"
                          type="button"
                          className="customtable-sort"
                          text={column.header}
                          shortText={column.shortHeader}
                          aria-label={column.header}
                          disabled={busy}
                          onClick={() => {
                            setSort({
                              key: column.key,
                              direction:
                                sort?.key === column.key ? -sort.direction : 1
                            });
                            setPage(1);
                          }}
                        >
                          {(headerText) => (
                            <>
                              <span className="customtable-header-text">
                                {headerText}
                              </span>

                              <span
                                className="customtable-header-measure"
                                aria-hidden="true"
                              >
                                {column.header}
                              </span>

                              <span
                                className="customtable-sort-icon"
                                aria-hidden="true"
                              >
                                {sort?.key === column.key
                                  ? sort.direction === 1
                                    ? " ↑"
                                    : " ↓"
                                  : " ↕"}
                              </span>
                            </>
                          )}
                        </OverflowText>
                      ) : (
                        <OverflowText
                          as="span"
                          className="customtable-sort"
                          text={column.header}
                          shortText={column.shortHeader}
                        >
                          {(headerText) => (
                            <>
                              <span className="customtable-header-text">
                                {headerText}
                              </span>

                              <span
                                className="customtable-header-measure"
                                aria-hidden="true"
                              >
                                {column.header}
                              </span>
                            </>
                          )}
                        </OverflowText>
                      )}

                      {isFilterable(column) && (
                        <button
                          type="button"
                          data-popup-trigger
                          aria-label={`${column.header}: ${labels.filter}`}
                          aria-haspopup="dialog"
                          aria-expanded={
                            popup?.type === "filter" &&
                            popup.column.key === column.key
                          }
                          className={
                            filters[column.key]
                              ? "customtable-filter-active"
                              : ""
                          }
                          disabled={busy}
                          onClick={(event) => {
                            setQuery("");
                            setDraft(
                              filters[column.key] ??
                                optionsFor(column).map(([key]) => key)
                            );
                            openPopup(event, {
                              type: "filter",
                              column
                            });
                          }}
                        >
                          ⏷
                        </button>
                      )}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading || !rows.length ? (
              <tr>
                <td
                  className="customtable-empty"
                  colSpan={Math.max(1, columns.length)}
                >
                  {loading
                    ? labels.loading
                    : data.length
                      ? labels.noMatches
                      : config.emptyText}
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={getId(row)}
                  className={
                    config.onRowClick ? "customtable-clickable" : undefined
                  }
                  data-record="true"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (
                      event.target === event.currentTarget &&
                      (event.key === "Enter" || event.key === " ")
                    ) {
                      event.preventDefault();
                      config.onRowClick?.(row);
                    }
                  }}
                  onClick={(event) => {
                    if (
                      event.target.closest(
                        "button, input, a, select, textarea, [data-editing]"
                      )
                    )
                      return;
                    clearTimeout(clickTimer.current);
                    if (event.target.closest("[data-editable]")) {
                      clickTimer.current = setTimeout(
                        () => config.onRowClick?.(row),
                        250
                      );
                    } else config.onRowClick?.(row);
                  }}
                >
                  {columns.map((column) => {
                    const editing =
                      edit?.rowId === getId(row) && edit?.column === column.key;

                    const actions = column.actionlist?.filter(
                      (action) => !action.visible || action.visible(row)
                    );

                    const style = recordStyle(column, row);

                    const rawValue = row[column.key];
                    const formattedValue = displayValue(rawValue, column);
                    return (
                      <td
                        key={column.key}
                        style={style}
                        data-color-mode={
                          resolveColor(column.colorRule, row, column.key)?.mode
                        }
                        data-color-fill={
                          style["--customtable-fill"] ||
                          style["--customtable-fill-color"]
                            ? "true"
                            : undefined
                        }
                        data-fixed={column.fixed ? "true" : undefined}
                        className={
                          filters[column.key]
                            ? "customtable-filtered-column"
                            : undefined
                        }
                        data-editable={column.onEdit ? "true" : undefined}
                        data-editing={editing ? "true" : undefined}
                        onDoubleClick={
                          column.onEdit
                            ? () => beginEdit(row, column)
                            : undefined
                        }
                      >
                        {actions ? (
                          actions.length > 0 && (
                            <button
                              type="button"
                              data-popup-trigger
                              className={`customtable-action ${column.appearOnHover ? "customtable-hover" : ""}`}
                              aria-label={labels.actions}
                              aria-haspopup="dialog"
                              aria-expanded={
                                popup?.type === "actions" &&
                                popup.row === row &&
                                popup.column.key === column.key
                              }
                              onClick={(event) =>
                                openPopup(event, {
                                  type: "actions",
                                  row,
                                  column
                                })
                              }
                            >
                              {column.text ?? "..."}
                            </button>
                          )
                        ) : editing ? (
                          <>
                            <input
                              autoFocus
                              className="customtable-edit"
                              aria-label={`${column.header}: ${labels.edit}`}
                              aria-invalid={!!error}
                              aria-describedby={
                                error ? `${id}-error` : undefined
                              }
                              value={edit.value}
                              disabled={busy}
                              onChange={(event) =>
                                setEdit({ ...edit, value: event.target.value })
                              }
                              onBlur={() => saveEdit(column)}
                              onKeyDown={(event) => {
                                event.stopPropagation();
                                if (event.key === "Enter") {
                                  event.preventDefault();
                                  saveEdit(column);
                                }
                                if (event.key === "Escape") {
                                  cancelEdit.current = true;
                                  setEdit(null);
                                  setError("");
                                }
                              }}
                            />
                            {error && (
                              <span
                                id={`${id}-error`}
                                className="customtable-error"
                                role="alert"
                              >
                                {error}
                              </span>
                            )}
                          </>
                        ) : column.render ? (
                          column.render(rawValue, row)
                        ) : column.onEdit ? (
                          <OverflowText
                            className="customtable-cell-text"
                            truncate={column.truncate}
                            text={formattedValue}
                            tabIndex={0}
                            role="button"
                            aria-label={`${column.header}: ${labels.edit}`}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                event.stopPropagation();
                                beginEdit(row, column);
                              }
                            }}
                          >
                            {formattedValue || "\u00a0"}{" "}
                          </OverflowText>
                        ) : (
                          <OverflowText
                            className="customtable-cell-text"
                            truncate={column.truncate}
                            text={formattedValue}
                            tabIndex={0}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {navigation("alsó")}
      {popup && (
        <div
          className="customtable-popup"
          role="dialog"
          aria-label={
            popup.type === "filter"
              ? `${popup.column.header}: ${labels.filter}`
              : labels.actions
          }
          style={{
            "--customtable-popup-left": `${popup.left}px`,
            "--customtable-popup-top":
              popup.top == null ? undefined : `${popup.top}px`,
            "--customtable-popup-bottom":
              popup.bottom == null ? undefined : `${popup.bottom}px`
          }}
        >
          {popup.type === "actions" ? (
            <div className="customtable-menu">
              {popup.column.actionlist
                .filter(
                  (action) => !action.visible || action.visible(popup.row)
                )
                .map((action, index) => (
                  <button
                    type="button"
                    autoFocus={index === 0}
                    key={action.key}
                    onClick={() => {
                      setPopup(null);
                      popup.trigger?.focus();
                      action.onClick(popup.row);
                    }}
                  >
                    {action.icon && (
                      <i className={`bi ${action.icon}`} aria-hidden="true" />
                    )}{" "}
                    {action.label}
                  </button>
                ))}
            </div>
          ) : (
            <>
              <input
                autoFocus
                type="search"
                aria-label={labels.search}
                placeholder={labels.search}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              <label>
                <input
                  type="checkbox"
                  checked={optionsFor(popup.column).every(([key]) =>
                    draft.includes(key)
                  )}
                  onChange={(event) =>
                    setDraft(
                      event.target.checked
                        ? optionsFor(popup.column).map(([key]) => key)
                        : []
                    )
                  }
                />{" "}
                {labels.all}
              </label>
              <div className="customtable-options">
                {optionsFor(popup.column)
                  .filter(([, value]) =>
                    displayValue(value, popup.column)
                      .toLocaleLowerCase()
                      .includes(query.toLocaleLowerCase())
                  )
                  .map(([key, value]) => (
                    <label key={key}>
                      <input
                        type="checkbox"
                        checked={draft.includes(key)}
                        onChange={(event) =>
                          setDraft(
                            event.target.checked
                              ? [...draft, key]
                              : draft.filter((entry) => entry !== key)
                          )
                        }
                      />
                      {displayValue(value, popup.column)}
                    </label>
                  ))}
              </div>
              <button
                type="button"
                onClick={() => {
                  const availableKeys = optionsFor(popup.column).map(
                    ([key]) => key
                  );
                  const selectedKeys = availableKeys.filter((key) =>
                    draft.includes(key)
                  );
                  setFilters({
                    ...filters,
                    [popup.column.key]:
                      availableKeys.length > 0 &&
                      selectedKeys.length === availableKeys.length
                        ? undefined
                        : selectedKeys
                  });
                  setPage(1);
                  setPopup(null);
                  popup.trigger?.focus();
                }}
              >
                {labels.apply}
              </button>{" "}
              <button
                type="button"
                onClick={() => {
                  setFilters({ ...filters, [popup.column.key]: undefined });
                  setPage(1);
                  setPopup(null);
                  popup.trigger?.focus();
                }}
              >
                {labels.clear}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
